import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Check, Clock, X, Mail, Eye } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingManager({ authHeader, type = "bookings" }) {
  const [items, setItems] = useState([]);

  const fetchItems = useCallback(async () => {
    try {
      const endpoint = type === "bookings" ? "admin/bookings" : "admin/contacts";
      const res = await axios.get(`${API}/${endpoint}`, authHeader);
      setItems(type === "bookings" ? res.data.bookings : res.data.contacts);
    } catch (e) {
      console.error(e);
    }
  }, [authHeader, type]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const updateBookingStatus = async (id, status) => {
    await axios.put(`${API}/admin/bookings/${id}/status`, { status }, authHeader);
    fetchItems();
  };

  const markRead = async (id) => {
    await axios.put(`${API}/admin/contacts/${id}/read`, {}, authHeader);
    fetchItems();
  };

  const statusColors = {
    pending: "bg-yellow-100 text-yellow-700",
    confirmed: "bg-green-100 text-green-700",
    declined: "bg-red-100 text-red-700",
    cancelled: "bg-gray-100 text-gray-500",
  };

  if (type === "contacts") {
    return (
      <div data-testid="contacts-manager">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="font-display text-wc-forest text-lg font-semibold mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5" /> Contact Messages ({items.length})
          </h3>
          {items.length === 0 ? (
            <p className="font-body text-gray-400 text-sm">No messages yet.</p>
          ) : (
            <div className="space-y-3">
              {items.map((item) => (
                <div key={item.id} className={`border rounded-md p-4 ${item.read ? "border-gray-100 bg-gray-50/50" : "border-wc-teal/20 bg-white"}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-display text-wc-forest font-semibold text-sm">{item.name}</span>
                        {!item.read && <span className="w-2 h-2 rounded-full bg-wc-teal" />}
                      </div>
                      <p className="font-body text-gray-500 text-xs mb-2">{item.email} {item.phone && `· ${item.phone}`}</p>
                      <p className="font-body text-gray-600 text-sm">{item.message}</p>
                      <p className="font-body text-gray-300 text-xs mt-2">{new Date(item.created_at).toLocaleString()}</p>
                    </div>
                    {!item.read && (
                      <Button size="sm" variant="ghost" onClick={() => markRead(item.id)}
                        className="text-wc-teal hover:bg-wc-teal/10 h-8 w-8 p-0" title="Mark as read">
                        <Eye className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="bookings-manager">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="font-display text-wc-forest text-lg font-semibold mb-4">
          Booking Inquiries ({items.length})
        </h3>
        {items.length === 0 ? (
          <p className="font-body text-gray-400 text-sm">No booking inquiries yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left font-body font-medium text-gray-500 pb-2 pr-4">Guest</th>
                  <th className="text-left font-body font-medium text-gray-500 pb-2 pr-4">Dates</th>
                  <th className="text-left font-body font-medium text-gray-500 pb-2 pr-4">Group</th>
                  <th className="text-left font-body font-medium text-gray-500 pb-2 pr-4">Status</th>
                  <th className="text-left font-body font-medium text-gray-500 pb-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-gray-50">
                    <td className="py-3 pr-4">
                      <p className="font-display text-wc-forest font-semibold text-sm">{item.name}</p>
                      <p className="font-body text-gray-400 text-xs">{item.email}</p>
                      {item.phone && <p className="font-body text-gray-400 text-xs">{item.phone}</p>}
                    </td>
                    <td className="py-3 pr-4 font-body text-gray-600">
                      {item.check_in} — {item.check_out}
                    </td>
                    <td className="py-3 pr-4 font-body text-gray-600">{item.group_size}</td>
                    <td className="py-3 pr-4">
                      <span className={`px-2 py-0.5 rounded text-xs font-body ${statusColors[item.status] || statusColors.pending}`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="py-3">
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => updateBookingStatus(item.id, "confirmed")}
                          className="text-green-600 hover:bg-green-50 h-7 w-7 p-0" title="Confirm">
                          <Check className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => updateBookingStatus(item.id, "declined")}
                          className="text-red-500 hover:bg-red-50 h-7 w-7 p-0" title="Decline">
                          <X className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => updateBookingStatus(item.id, "pending")}
                          className="text-yellow-600 hover:bg-yellow-50 h-7 w-7 p-0" title="Set Pending">
                          <Clock className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
